import React, {useEffect, useMemo} from 'react';
import {CircleMarker, FeatureGroup, MapContainer, Marker, Polyline, Tooltip, useMap} from 'react-leaflet';
import L, {divIcon} from 'leaflet';
import '../../../../node_modules/leaflet/dist/leaflet.css';
import CustomTileLayer from '../layout/map/CustomTileLayer.js';
import {COULEUR_NON_AFFECTE, COULEUR_PATRIMOINE, cleCalque} from './atelierRules';

/**
 * Carte de l'atelier (Leaflet) : calques patrimoine (gris), collecte (couleur par jour, parité par le style du point) et
 * tournées (points numérotés de la couleur de la journée, tracé facultatif, trajet base en pointillé), visites non
 * affectées en rouge. Aucune règle ici : les calques arrivent calculés par `atelierRules`.
 * AJOUT À VALIDER (GUIDE_UI) : carte plein volet avec calques superposables.
 */

const iconsNumero = new Map();
function iconNumero(numero, couleur, fige) {
    const cle = `${numero}|${couleur}|${fige}`;
    if (!iconsNumero.has(cle)) {
        iconsNumero.set(cle, divIcon({
            className: 'simulation-marker',
            html: `<span style="display:flex;align-items:center;justify-content:center;min-width:24px;height:24px;padding:0 3px;box-sizing:border-box;border-radius:12px;background:${couleur};color:#fff;font-weight:700;font-size:11px;border:2px solid ${fige ? '#E0A100' : '#fff'}">${numero}</span>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
        }));
    }
    return iconsNumero.get(cle);
}

const iconBase = divIcon({
    className: 'simulation-marker',
    html: '<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:6px;background:#fff;color:#213067;font-weight:800;font-size:12px;border:2px solid #213067">B</span>',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
});

/** Style d'un point de collecte : plein = hebdo, anneau = paire, mi-teinte pointillée = impaire. */
export function styleCollecte(couleur, frequence) {
    if (frequence === 'paire') return {color: couleur, fillColor: '#fff', fillOpacity: 1, weight: 3};
    if (frequence === 'impaire') return {color: couleur, fillColor: couleur, fillOpacity: 0.35, weight: 2, dashArray: '2 3'};
    return {color: '#fff', fillColor: couleur, fillOpacity: 0.9, weight: 1};
}

function Ajuste({positions, cadrageCle}) {
    const map = useMap();
    useEffect(() => {
        if (positions.length === 1) map.setView(positions[0], 14);
        else if (positions.length > 1) map.fitBounds(L.latLngBounds(positions), {padding: [40, 40]});
        // Le cadrage ne suit pas les bascules de calques : seulement le changement de patrimoine / de scénario affiché.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, cadrageCle]);
    return null;
}

/**
 * Un seul gestionnaire de clic par calque : le FeatureGroup propage les clics de ses marqueurs (`propagatedFrom`), qui portent
 * leur `nomenclature` (et la journée pour les tournées) dans leurs options. Chaque calque est mémoïsé : une bascule ou
 * une frappe dans le volet ne redessine pas les autres.
 */
const groupeClic = (onSelectPav) => ({
    click: (e) => {
        const o = e.propagatedFrom?.options;
        if (o?.nomenclature) onSelectPav(o.nomenclature, o.journee ?? null);
    },
});

const CalquePatrimoine = React.memo(function CalquePatrimoine({pavs, onSelectPav}) {
    const handlers = useMemo(() => groupeClic(onSelectPav), [onSelectPav]);
    return (
        <FeatureGroup eventHandlers={handlers}>
            {pavs.map((p) => (
                <CircleMarker key={p.nomenclature} nomenclature={p.nomenclature} center={[p.lat, p.lon]} radius={4}
                              pathOptions={{color: '#fff', weight: 1, fillColor: COULEUR_PATRIMOINE, fillOpacity: 0.85}}>
                    <Tooltip>{p.nomenclature}</Tooltip>
                </CircleMarker>
            ))}
        </FeatureGroup>
    );
});

const CalqueCollecte = React.memo(function CalqueCollecte({calque, parNomenclature, onSelectPav}) {
    const handlers = useMemo(() => groupeClic(onSelectPav), [onSelectPav]);
    const style = useMemo(() => styleCollecte(calque.couleur, calque.frequence), [calque]);
    return (
        <FeatureGroup eventHandlers={handlers}>
            {calque.nomenclatures.map((n) => {
                const p = parNomenclature.get(n);
                return p && (
                    <CircleMarker key={n} nomenclature={n} center={[p.lat, p.lon]} radius={6} pathOptions={style}>
                        <Tooltip>{n} · {calque.libelle.split(' · ').slice(0, 2).join(' · ')}</Tooltip>
                    </CircleMarker>
                );
            })}
        </FeatureGroup>
    );
});

const CalqueTournee = React.memo(function CalqueTournee({calque, avecTraces, active, onSelectPav}) {
    const handlers = useMemo(() => groupeClic(onSelectPav), [onSelectPav]);
    return (
        <>
            {calque.segmentsBase.map((seg, i) => (
                <Polyline key={`b-${i}`} positions={seg} pathOptions={{color: calque.couleur, weight: 2, dashArray: '6 6', opacity: 0.8}}/>
            ))}
            {avecTraces && calque.trace.length > 1 &&
                <Polyline positions={calque.trace} pathOptions={{color: calque.couleur, weight: active ? 4 : 2, opacity: active ? 0.95 : 0.6}}/>}
            <FeatureGroup eventHandlers={handlers}>
                {calque.points.map((p) => (
                    <Marker key={p.numero} nomenclature={p.nomenclature} journee={calque.cle} position={p.position}
                            icon={iconNumero(p.numero, calque.couleur, p.fige)}>
                        <Tooltip>{p.numero}. {p.nomenclature}{p.arrivee ? ` · ${p.arrivee}` : ''}</Tooltip>
                    </Marker>
                ))}
            </FeatureGroup>
        </>
    );
});

const CalqueNonAffectes = React.memo(function CalqueNonAffectes({points, onSelectPav}) {
    const handlers = useMemo(() => groupeClic(onSelectPav), [onSelectPav]);
    return (
        <FeatureGroup eventHandlers={handlers}>
            {points.map((n) => (
                <CircleMarker key={n.nomenclature} nomenclature={n.nomenclature} center={n.position} radius={6}
                              pathOptions={{color: '#fff', weight: 1.5, fillColor: COULEUR_NON_AFFECTE, fillOpacity: 0.95}}>
                    <Tooltip>{n.nomenclature} · non affecté</Tooltip>
                </CircleMarker>
            ))}
        </FeatureGroup>
    );
});

function AtelierCarte({
    pavs, calquesCollecte, calquesTournees, nonAffectes, visible, avecTraces, selection, onSelectPav,
    journeeActive, cadrageCle,
}) {
    const parNomenclature = useMemo(() => new Map((pavs || []).map((p) => [p.nomenclature, p])), [pavs]);
    const positions = useMemo(() => (pavs || []).map((p) => [p.lat, p.lon]), [pavs]);
    const basePos = useMemo(() => (calquesTournees || []).map((c) => c.depart).find(Boolean) ?? null, [calquesTournees]);
    const positionChoisie = useMemo(() => {
        if (!selection) return null;
        const p = parNomenclature.get(selection);
        if (p) return [p.lat, p.lon];
        return (calquesTournees || []).flatMap((c) => c.points).find((x) => x.nomenclature === selection)?.position
            ?? (nonAffectes || []).find((n) => n.nomenclature === selection)?.position ?? null;
    }, [selection, parNomenclature, calquesTournees, nonAffectes]);

    return (
        <MapContainer center={[46.6, 2.4]} zoom={6} preferCanvas zoomAnimation={false} fadeAnimation={false} markerZoomAnimation={false} style={{height: '100%', width: '100%', minHeight: 320}}>
            <CustomTileLayer maxZoom={19}/>
            <Ajuste positions={positions} cadrageCle={cadrageCle}/>

            {visible(cleCalque.patrimoine) && <CalquePatrimoine pavs={pavs || []} onSelectPav={onSelectPav}/>}

            {(calquesCollecte || []).filter((c) => visible(cleCalque.collecte(c.cle))).map((c) => (
                <CalqueCollecte key={`c-${c.cle}`} calque={c} parNomenclature={parNomenclature} onSelectPav={onSelectPav}/>
            ))}

            {(calquesTournees || []).filter((c) => visible(cleCalque.tournee(c.cle))).map((c) => (
                <CalqueTournee key={`t-${c.cle}`} calque={c} avecTraces={avecTraces} active={journeeActive === c.cle} onSelectPav={onSelectPav}/>
            ))}

            {(nonAffectes || []).length > 0 && <CalqueNonAffectes points={nonAffectes} onSelectPav={onSelectPav}/>}

            {basePos && <Marker position={basePos} icon={iconBase}><Tooltip>Base de départ</Tooltip></Marker>}

            {positionChoisie &&
                <CircleMarker center={positionChoisie} radius={13} interactive={false}
                              pathOptions={{color: '#0F2D69', weight: 3, fill: false}}/>}
        </MapContainer>
    );
}

export default React.memo(AtelierCarte);
